import Sentry from "@similarweb/sentry-logger";

import { getRecentKeywordListEntities, type RecentKeywordListEntity } from "app/modules/demand-analysis/keyword-lists";
import { DefaultFetchService } from "app/services/fetchService";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { keywordGroupsQueryClient } from "app/services/keyword-groups/utils/keywordGroupsQueryClient";
import { RecentService } from "app/services/recent/recentService";
import type { IRecentObject } from "app/@types/userdata";

import { getAutocompleteKeywordsForQuery } from "../../AutocompleteKeywords/helpers/KeywordAutocompleteDataHandler";
import type { IAutocompleteKeywordGroup } from "../../AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import {
    AutocompleteDemandAnalysisEntityType,
    ENTITY_ICONS,
    type AiTopicSearchResult,
    type AutocompleteDemandAnalysisItem,
} from "../types/AutocompleteDemandAnalysisTypes";
import { transformToUnifiedResults } from "../utils/autocompleteDemandAnalysisDataTransformers";
import { matchesCaseInsensitive } from "../utils/autocompleteDemandAnalysisDataUtils";
import {
    AUTOCOMPLETE_LIMIT_MODE,
    AUTOCOMPLETE_NO_QUERY_RECENTS_TOTAL_LIMIT,
    type AutocompleteLimitMode,
} from "../utils/consts";

/**
 * Checks if a query is too short for search results.
 * Returns true if the query exists but has less than 2 characters,
 * indicating that we should return empty results instead of recent searches.
 */
const isQueryTooShort = (query: string): boolean => {
    return Boolean(query) && query.trim().length > 0 && query.trim().length < 2;
};

/**
 * Deduplicate items by name since recent items can have duplicates, keeping the first occurrence (most recent)
 * Works with both 'name' and 'Name' properties
 */
const deduplicateByName = <T extends { name: string } | { Name: string }>(items: T[]): T[] => {
    const seen = new Set<string>();
    return items.filter((item) => {
        const itemName = "name" in item ? item.name : item.Name;
        if (seen.has(itemName)) {
            return false;
        }
        seen.add(itemName);
        return true;
    });
};

const EMPTY_SEARCH_RELEVANCE = {
    isExactMatch: false,
    startsWithQuery: false,
    matchIndex: -1,
};

const toAutocompleteKeywordGroup = (group: IUserKeywordGroup, sharedWithMe: boolean): IAutocompleteKeywordGroup => {
    return { ...group, SharedWithMe: sharedWithMe } as IAutocompleteKeywordGroup;
};

const getUngroupedRecentsItems = async (
    keywordsGroupsToFilterFromAutocomplete?: string[],
): Promise<AutocompleteDemandAnalysisItem[]> => {
    try {
        const recents = RecentService.getRecents() as IRecentObject[];
        const recentEntities = getRecentKeywordListEntities(recents);
        const { userGroups, sharedGroups } = await keywordGroupsQueryClient.getAllGroups();

        const ownGroupsById = new Map(userGroups.map((group) => [group.Id, toAutocompleteKeywordGroup(group, false)]));
        const sharedGroupsById = new Map(
            sharedGroups.map((group) => [group.Id, toAutocompleteKeywordGroup(group, true)]),
        );

        const excludedIds = new Set(keywordsGroupsToFilterFromAutocomplete ?? []);
        const seenKeywordListIds = new Set<string>();
        const items: AutocompleteDemandAnalysisItem[] = [];

        for (const { entity } of recentEntities) {
            if (entity.type === "keywordGroup") {
                const ownGroup = ownGroupsById.get(entity.id);
                const sharedGroup = sharedGroupsById.get(entity.id);
                const keywordGroup = ownGroup ?? sharedGroup;
                if (!keywordGroup || excludedIds.has(keywordGroup.Id)) {
                    continue;
                }

                const type = ownGroup
                    ? AutocompleteDemandAnalysisEntityType.ownList
                    : AutocompleteDemandAnalysisEntityType.sharedList;
                const keywordListId = entity.keywordListId;
                if (seenKeywordListIds.has(keywordListId)) {
                    continue;
                }

                seenKeywordListIds.add(keywordListId);
                items.push({
                    id: keywordListId,
                    name: keywordGroup.Name,
                    type,
                    icon: ENTITY_ICONS[type],
                    searchRelevance: EMPTY_SEARCH_RELEVANCE,
                    originalData: keywordGroup,
                });
            }

            if (entity.type === "aiTopic") {
                if (excludedIds.has(entity.name)) {
                    continue;
                }

                const keywordListId = entity.keywordListId;
                if (seenKeywordListIds.has(keywordListId)) {
                    continue;
                }

                seenKeywordListIds.add(keywordListId);
                items.push({
                    id: keywordListId,
                    name: entity.name,
                    type: AutocompleteDemandAnalysisEntityType.aiTopic,
                    icon: ENTITY_ICONS[AutocompleteDemandAnalysisEntityType.aiTopic],
                    searchRelevance: EMPTY_SEARCH_RELEVANCE,
                    originalData: { seed: entity.name },
                });
            }

            if (items.length >= AUTOCOMPLETE_NO_QUERY_RECENTS_TOTAL_LIMIT) {
                break;
            }
        }

        return items;
    } catch (error) {
        Sentry.errorTracker({
            message: "Error in getUngroupedRecentsItems",
            e: error,
        });
        return [];
    }
};

const getAutocompleteKeywordGroups = async (
    query: string,
    hasQuery: boolean,
    recentEntities: RecentKeywordListEntity[],
    keywordsGroupsToFilterFromAutocomplete?: string[],
): Promise<IAutocompleteKeywordGroup[]> => {
    if (isQueryTooShort(query)) {
        return [];
    }

    const userGroups = await keywordGroupsQueryClient.getUserGroups();
    const userGroupIds = new Set(userGroups.map((group) => group.Id));

    if (hasQuery) {
        const keywordGroups = userGroups.map((group) => toAutocompleteKeywordGroup(group, false));
        const result = keywordGroups.filter(({ Name }) => matchesCaseInsensitive(Name, query));
        return keywordsGroupsToFilterFromAutocomplete
            ? result.filter(({ Id }) => !keywordsGroupsToFilterFromAutocomplete.includes(Id))
            : result;
    }

    // Without a query, return recent own keyword groups
    const recentKeywordGroupIds = recentEntities
        .filter(({ entity }) => entity.type === "keywordGroup" && userGroupIds.has(entity.id))
        .map(({ entity }) => entity.id);

    const keywordGroups = userGroups.map((group) => toAutocompleteKeywordGroup(group, false));
    const recentKeywordGroups = recentKeywordGroupIds
        .map((Id) => keywordGroups.find((group) => group.Id === Id))
        .filter(Boolean) as IAutocompleteKeywordGroup[];
    const deduplicatedGroups = deduplicateByName(recentKeywordGroups);
    return keywordsGroupsToFilterFromAutocomplete
        ? deduplicatedGroups.filter(({ Id }) => !keywordsGroupsToFilterFromAutocomplete.includes(Id))
        : deduplicatedGroups;
};

const getAutocompleteSharedKeywordGroups = async (
    query: string,
    hasQuery: boolean,
    recentEntities: RecentKeywordListEntity[],
    keywordsGroupsToFilterFromAutocomplete?: string[],
): Promise<IAutocompleteKeywordGroup[]> => {
    if (isQueryTooShort(query)) {
        return [];
    }

    const sharedGroups = await keywordGroupsQueryClient.getSharedGroups();
    const sharedKwGroups: IAutocompleteKeywordGroup[] = sharedGroups.map((group) =>
        toAutocompleteKeywordGroup(group, true),
    );
    const sharedGroupIds = new Set(sharedKwGroups.map((group) => group.Id));

    if (hasQuery) {
        const result = sharedKwGroups.filter(({ Name }) => matchesCaseInsensitive(Name, query));
        return keywordsGroupsToFilterFromAutocomplete
            ? result.filter(({ Id }) => !keywordsGroupsToFilterFromAutocomplete.includes(Id))
            : result;
    }

    // Without a query, return recent shared keyword groups
    const recentSharedGroupIds = recentEntities
        .filter(({ entity }) => entity.type === "keywordGroup" && sharedGroupIds.has(entity.id))
        .map(({ entity }) => entity.id);

    // Preserve recents order by mapping in the same order as recentSharedGroupIds
    const recentSharedGroups = recentSharedGroupIds
        .map((id) => sharedKwGroups.find(({ Id }) => Id === id))
        .filter(Boolean) as IAutocompleteKeywordGroup[];

    const deduplicatedSharedGroups = deduplicateByName(recentSharedGroups);

    return keywordsGroupsToFilterFromAutocomplete
        ? deduplicatedSharedGroups.filter(({ Id }) => !keywordsGroupsToFilterFromAutocomplete.includes(Id))
        : deduplicatedSharedGroups;
};

const getAutocompleteAiTopics = async (
    query: string,
    hasQuery: boolean,
    recentEntities: RecentKeywordListEntity[],
    keywordsGroupsToFilterFromAutocomplete?: string[],
): Promise<AiTopicSearchResult[]> => {
    if (isQueryTooShort(query)) {
        return [];
    }

    try {
        if (hasQuery) {
            const fetchService = DefaultFetchService.getInstance();
            const result = await getAutocompleteKeywordsForQuery(query, fetchService);

            return result;
        }

        const recentAiTopics = recentEntities
            .filter(({ entity }) => entity.type === "aiTopic")
            .map(({ entity }) => ({ name: entity.name }));

        const deduplicatedAiTopics = deduplicateByName(recentAiTopics);
        return keywordsGroupsToFilterFromAutocomplete
            ? deduplicatedAiTopics.filter(({ name }) => !keywordsGroupsToFilterFromAutocomplete.includes(name))
            : deduplicatedAiTopics;
    } catch (error) {
        Sentry.errorTracker({
            message: "Error in getAutocompleteAiTopics",
            e: error,
            params: {
                extra: {
                    query,
                    hasQuery,
                    keywordsGroupsToFilterFromAutocomplete,
                },
            },
        });

        return [];
    }
};

const getAllAutocompleteData = async (
    searchText: string,
    hasQuery: boolean,
    keywordsGroupsToFilterFromAutocomplete?: string[],
) => {
    try {
        const recents = RecentService.getRecents() as IRecentObject[];
        const recentEntities = getRecentKeywordListEntities(recents);

        const [keywordGroups, sharedKeywordGroups, aiTopics] = await Promise.all([
            getAutocompleteKeywordGroups(searchText, hasQuery, recentEntities, keywordsGroupsToFilterFromAutocomplete),
            getAutocompleteSharedKeywordGroups(
                searchText,
                hasQuery,
                recentEntities,
                keywordsGroupsToFilterFromAutocomplete,
            ),
            getAutocompleteAiTopics(searchText, hasQuery, recentEntities, keywordsGroupsToFilterFromAutocomplete),
        ]);

        return {
            aiTopics,
            keywordGroups,
            sharedKeywordGroups,
        };
    } catch (error) {
        Sentry.errorTracker({
            message: "Error in getAllAutocompleteData",
            e: error,
            params: {
                extra: {
                    searchText,
                    hasQuery,
                    keywordsGroupsToFilterFromAutocomplete,
                },
            },
        });
        return {
            aiTopics: [],
            keywordGroups: [],
            sharedKeywordGroups: [],
        };
    }
};

/**
 * Main entry point for autocomplete data fetching.
 * Fetches data from multiple sources and returns unified results.
 * Does not handle state management - that's the component's responsibility.
 */
export const getAutocompleteData = async (
    searchText: string,
    hasQuery: boolean,
    limitMode: AutocompleteLimitMode = AUTOCOMPLETE_LIMIT_MODE.unlimited,
    keywordsGroupsToFilterFromAutocomplete?: string[],
): Promise<AutocompleteDemandAnalysisItem[]> => {
    if (isQueryTooShort(searchText)) {
        return [];
    }

    if (!hasQuery) {
        return getUngroupedRecentsItems(keywordsGroupsToFilterFromAutocomplete);
    }

    try {
        const { keywordGroups, sharedKeywordGroups, aiTopics } = await getAllAutocompleteData(
            searchText,
            hasQuery,
            keywordsGroupsToFilterFromAutocomplete,
        );

        return transformToUnifiedResults(keywordGroups, sharedKeywordGroups, aiTopics, searchText, limitMode);
    } catch (error) {
        Sentry.errorTracker({
            message: "Error in getAutocompleteData",
            e: error,
            params: {
                extra: {
                    searchText,
                    hasQuery,
                    keywordsGroupsToFilterFromAutocomplete,
                },
            },
        });
        return [];
    }
};
